import React from 'react';
import { Tag, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { WALLET_TRANSACTION_STATUSES, WALLET_TRANSACTION_TYPES } from 'constants/wallet';
import { getTransactionStatusKey, getTransactionTypeKey } from 'pages/Wallet/helper';
import styles from './style.module.scss';

const STATUS_TAG_COLORS = {
  [WALLET_TRANSACTION_STATUSES.completed]: 'green',
  [WALLET_TRANSACTION_STATUSES.pending]: 'gold',
  [WALLET_TRANSACTION_STATUSES.rejected]: 'red',
};

const translateOrRaw = (translateText, key, raw) => (key ? translateText(key) : raw);

// `orderBy` is the API's 'amount Asc' / 'amount Desc' (or null); antd wants 'ascend' / 'descend'.
const AMOUNT_SORT_ORDERS = { 'amount Asc': 'ascend', 'amount Desc': 'descend' };

const tableColumns = ({
  translateText,
  symbol,
  showTypeFilter,
  transactionTypeFilters,
  showStatusFilter,
  transactionStatusFilters,
  showAmountSorter,
  orderBy,
}) => [
  {
    title: translateText('corporateAccounts.wallet.dashboard.history.date'),
    dataIndex: 'date',
    key: 'date',
  },
  {
    title: translateText('corporateAccounts.wallet.dashboard.history.type'),
    dataIndex: 'transactionType',
    key: 'transactionType',
    ...(showTypeFilter && {
      filteredValue: transactionTypeFilters,
      filters: Object.values(WALLET_TRANSACTION_TYPES).map(type => ({
        text: translateText(getTransactionTypeKey(type)),
        value: type,
      })),
    }),
    render: value => translateOrRaw(translateText, getTransactionTypeKey(value), value),
  },
  {
    title: translateText('corporateAccounts.wallet.dashboard.history.transactionId'),
    dataIndex: 'id',
    key: 'transactionId',
  },
  {
    title: translateText('corporateAccounts.wallet.dashboard.history.amount', { symbol }),
    dataIndex: 'amount',
    key: 'amount',
    ...(showAmountSorter && {
      sorter: true,
      sortOrder: AMOUNT_SORT_ORDERS[orderBy] ?? null,
      // No "Click to sort ascending" tooltip on the header; the sort arrows are enough
      showSorterTooltip: false,
    }),
  },
  {
    title: translateText('corporateAccounts.wallet.dashboard.history.balance', { symbol }),
    dataIndex: 'balance',
    key: 'balance',
  },
  {
    title: translateText('corporateAccounts.wallet.dashboard.history.status'),
    dataIndex: 'status',
    key: 'status',
    className: styles.historyStatusCol,
    ...(showStatusFilter && {
      filteredValue: transactionStatusFilters,
      filters: Object.values(WALLET_TRANSACTION_STATUSES).map(status => ({
        text: translateText(getTransactionStatusKey(status)),
        value: status,
      })),
    }),
    render: (status, record) => (
      <>
        <Tag color={STATUS_TAG_COLORS[status]}>
          {translateOrRaw(translateText, getTransactionStatusKey(status), status)}
        </Tag>
        {record.topUpRejectReason && (
          <Tooltip
            title={
              <>
                <div>{record.topUpRejectReason}</div>
                <div className={styles.historyRejectMeta}>
                  {translateText('corporateAccounts.wallet.dashboard.history.rejectedBy', {
                    date: record.date,
                  })}
                </div>
              </>
            }
          >
            <InfoCircleOutlined
              className={styles.historyRejectReason}
              data-testid="walletBalanceHistoryRejectReason"
            />
          </Tooltip>
        )}
      </>
    ),
  },
];

export default tableColumns;
